import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {select, Store} from '@ngrx/store';
import {Observable, of} from 'rxjs';
import {catchError, map, mergeMap, switchMap, tap, withLatestFrom} from 'rxjs/operators';
import * as GlobalMessageActions from '../../core/actions/global-message.actions';
import {GlobalMessage, GlobalMessageType} from '../../core/models/global-message';
import * as CollectionActions from '../actions/collection.actions';
import {Attribute} from '../models/attribute';
import {ProductsError} from '../models/products-error';
import * as fromProd from '../reducers';
import * as fromCollectionPage from '../reducers/collection-page.reducer';
import {ProductsApiService, ProductsResult} from '../services/products-api.service';
import {SearchFilterConverter} from '../services/search-filter-converter';
import {ProductQuery} from "../models/product-query";

@Injectable()
export class CollectionEffects {

    $loadCollection = createEffect(() => this.actions$
        .pipe(
            ofType(CollectionActions.load),
            tap(action => console.log('load products for collection page', action)),
            withLatestFrom(this.store.pipe(select(fromProd.getCollectionState)),
                this.store.pipe(select(fromProd.getAttributes))),
            switchMap(([action, collectionState, attributes]) => {
                return this.loadProducts(collectionState, attributes)
                    .pipe(
                        map((x: ProductsResult) => CollectionActions.loadSuccess({
                            command: action.command,
                            products: x.products,
                            totalProductCount: x.totalCount
                        })),
                        catchError(error => of(CollectionActions.loadFailure({
                            command: action.command,
                            error: (<ProductsError>error).errorMessage
                        })))
                    );
            })
        ));

    $loadFailure = createEffect(() => this.actions$
        .pipe(
            ofType(CollectionActions.loadFailure),
            mergeMap(action => {
                    const message: GlobalMessage = {
                        id: 'load-products-error',
                        type: GlobalMessageType.ERROR,
                        message: 'The products couldn\'t be loaded: ' + action.error
                    };
                    return of(GlobalMessageActions.addMessage({globalMessage: message}));
                }
            )
        ));

    $loadMore = createEffect(() => this.actions$
        .pipe(
            ofType(CollectionActions.add),
            tap(action => console.log('add products to collection page', action)),
            withLatestFrom(this.store.pipe(select(fromProd.getCollectionState)),
                this.store.pipe(select(fromProd.getAttributes))),
            switchMap(([action, collectionState, attributes]) => {
                return this.loadProducts(collectionState, attributes)
                    .pipe(
                        map((x: ProductsResult) => CollectionActions.addSuccess({
                            command: action.command,
                            products: x.products,
                            totalProductCount: x.totalCount
                        })),
                        catchError(error => of(CollectionActions.addFailure({
                            command: action.command,
                            error: (<ProductsError>error).errorMessage
                        })))
                    );
            })
        ));

    $addFailure = createEffect(() => this.actions$
        .pipe(
            ofType(CollectionActions.addFailure),
            mergeMap(action => {
                    const message: GlobalMessage = {
                        id: 'add-products-error',
                        type: GlobalMessageType.ERROR,
                        message: 'The products couldn\'t be loaded: ' + action.error
                    };
                    return of(GlobalMessageActions.addMessage({globalMessage: message}));
                }
            )
        ));

    constructor(private actions$: Actions,
                private store: Store<fromProd.State>,
                private productService: ProductsApiService,
                private searchFilterConverter: SearchFilterConverter) {
    }

    private loadProducts(collectionState: fromCollectionPage.State, attributes: Attribute[]): Observable<ProductsResult> {
        const searchQuery: ProductQuery = {
            page: collectionState.currentPage + 1,
            pageSize: collectionState.pageSize,
            filterGroups: this.searchFilterConverter.convertToFilterGroup(collectionState.searchFilters, attributes),
            sortOrders: collectionState.sortOrders
        };

        console.log('search query: ', searchQuery);

        return this.productService.getProducts(searchQuery);
    }
}
